'use es6';

import shallowEqual from 'react-utils/shallowEqual';
import calculateBasicNodePositions from './calculateBasicNodePositions';
const REQUIRED_CONFIG_FIELDS = ['getElements', 'getListFromNodeData'];
const validateConfig = config => {
  if (!config || typeof config !== 'object') {
    throw new Error('buildCalculateNodePositions requires a config object');
  }
  Object.keys(config).forEach(key => {
    // These fields are only required when using basicCalculateNodePositions
    if (!config[key].calculateNodePositions) {
      REQUIRED_CONFIG_FIELDS.forEach(field => {
        if (config[key][field] == null) {
          throw new Error(`Missing required node position config option: ${field} in ${key}`);
        }
      });
    }
    // Add the key to the config to allow for more useful error logging
    config[key].key = key;
  });
};

/**
 * Returns a calculateNodePositions function after validating config
 * @param  {Object} config {[node position key]: options} (options defined below)
 *   @prop {func} getElements: returns a list of all corresponding DOM elements for an item
 *   @prop {func} getListFromNodeData: returns the desired list of items from nodeData
 *   @prop {func} getCustomFields (optional) returns additional fields to include in the node position object
 *   @prop {func} getElementBounds (optional) returns the bounds for the given element
 *   @prop {func} calculateNodePositions (optional) overrides calculateBasicNodePositions
 *   @prop {bool} debug (optional) enables console logging
 * @return {func}
 */
export const buildCalculateNodePositions = (config, {
  beforeCalculateAnyNodePositions,
  afterCalculatingAllNodePositions
} = {}) => {
  validateConfig(config);

  /**
   * Calculates node positions for elements, and return previous nodePosition
   * reference if nothing has changed.
   * @param  {Object} nodeData {staticModules: [{id: '123'}, id: {'456'}]}
   * @param  {Object} prevNodePositions {STATIC_MODULES: [{top: 1, left: 1...}, {top: 1, left: 1...}]}
   * @return {Object} nodePositions {STATIC_MODULES: [{top: 2, left: 2...}, {top: 2, left: 2...}]}
   */
  return (nodeData, prevNodePositions = {}) => {
    const newNodePositions = {};
    if (beforeCalculateAnyNodePositions) {
      beforeCalculateAnyNodePositions(config);
    }
    Object.keys(config).forEach(key => {
      const subConfig = config[key];
      const prevSubNodePositions = prevNodePositions[key];
      const calculateNodePositionsFunc = subConfig.calculateNodePositions ? subConfig.calculateNodePositions : calculateBasicNodePositions;
      newNodePositions[key] = calculateNodePositionsFunc(nodeData, prevSubNodePositions, subConfig);
    });
    if (afterCalculatingAllNodePositions) {
      afterCalculatingAllNodePositions(config);
    }

    // If all the node positions are the same, return the previous reference
    if (shallowEqual(newNodePositions, prevNodePositions)) {
      return prevNodePositions;
    }
    return newNodePositions;
  };
};